import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/CategoryProducts.css';
import { FaArrowUp, FaArrowDown, FaFilter } from 'react-icons/fa';
import EweProductCard from './EweProductCard';

const CategoryProducts = ({
  products = [],
  categoryName,
  cart,
  setCart,
  isLoggedIn,
  handleCategoryClick,
  subcategories = [],
  refreshCart
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const productsPerPage = 20;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Initialize filteredProducts with all products
  useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  // Extract unique brands and attributes from EWE products
  const extractFiltersData = () => {
    const uniqueBrands = [...new Set(products.map(p => p.acDept).filter(Boolean))];

    const attributesMap = {};
    products.forEach((product) => {
      if (product.acInlineSpecification) {
        const specs = product.acInlineSpecification.split('\n').filter(Boolean);
        specs.forEach(spec => {
          const [key, value] = spec.split(':').map(s => s.trim());
          if (key && value) {
            if (!attributesMap[key]) {
              attributesMap[key] = new Set();
            }
            attributesMap[key].add(value);
          }
        });
      }
    });

    return {
      brands: uniqueBrands,
      attributes: attributesMap
    };
  };

  const { brands, attributes } = extractFiltersData();

  // Filter and sort products
  useEffect(() => {
    let result = [...products];

    // Apply brand filter
    if (selectedBrands.length > 0) {
      result = result.filter((product) =>
        product.acDept && selectedBrands.includes(product.acDept)
      );
    }

    // Apply attribute filters
    if (Object.keys(selectedAttributes).length > 0) {
      result = result.filter((product) => {
        if (!product.acInlineSpecification) return false;

        return Object.entries(selectedAttributes).every(([key, values]) => {
          if (values.length === 0) return true;

          const specLines = product.acInlineSpecification.split('\n');
          const matchingSpec = specLines.find(line =>
            line.includes(key) && values.some(v => line.includes(v))
          );

          return !!matchingSpec;
        });
      });
    }

    // Apply price sorting
    if (sortDirection === 'asc') {
      result.sort((a, b) => (a.anPrice || 0) - (b.anPrice || 0));
    } else if (sortDirection === 'desc') {
      result.sort((a, b) => (b.anPrice || 0) - (a.anPrice || 0));
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [products, sortDirection, selectedBrands, selectedAttributes]);

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand)
        ? prev.filter((b) => b !== brand)
        : [...prev, brand]
    );
  };

  const handleAttributeChange = (key, value) => {
    setSelectedAttributes((prev) => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];

      if (newValues.length === 0) {
        const newAttributes = {...prev};
        delete newAttributes[key];
        return newAttributes;
      }

      return {
        ...prev,
        [key]: newValues
      };
    });
  };

  const resetFilters = () => {
    setSortDirection(null);
    setSelectedBrands([]);
    setSelectedAttributes({});
  };

  // Calculate pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Function to refresh cart items after adding to cart
  const fetchCartItems = () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const guestCartId = localStorage.getItem("guestCartId");

    if (!userId && !guestCartId) return;

    const cartIdentifier = userId
      ? `userId=${userId}`
      : `guestCartId=${guestCartId}`;

    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

    axios
      .get(`${API_BASE_URL}/cart?${cartIdentifier}`, config)
      .then((response) => {
        if (response.data && response.data.items) {
          setCart(response.data.items);
        }
      })
      .catch((error) => {
        console.error("Error fetching cart items:", error);
      });
  };

  // Function to add product to cart
  const addToCart = async (product) => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    let guestCartId = localStorage.getItem("guestCartId");

    if (!isLoggedIn && !guestCartId) {
      guestCartId = 'guest-' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("guestCartId", guestCartId);
    }

    const payload = {
      productId: product.anProductKey,
      quantity: 1,
      price: product.anPrice,
      name: product.acName || product.acProduct,
      isEweProduct: true,
      ...(isLoggedIn ? { userId } : { guestCartId })
    };

    try {
      const response = await fetch(`${API_BASE_URL}/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? "Bearer " + token : "",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      // After successfully adding to cart, refresh the cart
      fetchCartItems();
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  };

  return (
    <div className="category-products-container">
      <div className="filter-section">
        <button
          className="filter-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {showFilters ? 'Sakrij filtere' : 'Prikaži filtere'}
        </button>
      </div>

      <div className="category-header">
        <h2>{categoryName}</h2>

        {subcategories.length > 0 && (
          <div className="subcategories-list">
            <h4>Podkategorije:</h4>
            <div className="subcategory-buttons">
              {subcategories.map(subcategory => (
                <button
                  key={subcategory.id}
                  className="subcategory-button"
                  onClick={() => handleCategoryClick({
                    ...subcategory,
                    type: subcategory.children?.length ? 'category' : 'subcategory'
                  })}
                >
                  {subcategory.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <h4>Sortiraj po ceni:</h4>
            <div className="price-filter">
              <button
                className={`sort-button ${sortDirection === 'asc' ? 'active' : ''}`}
                onClick={() => setSortDirection(sortDirection === 'asc' ? null : 'asc')}
              >
                <FaArrowUp /> Cena (Niža prema višoj)
              </button>
              <button
                className={`sort-button ${sortDirection === 'desc' ? 'active' : ''}`}
                onClick={() => setSortDirection(sortDirection === 'desc' ? null : 'desc')}
              >
                <FaArrowDown /> Cena (Viša prema nižoj)
              </button>
            </div>
          </div>

          {brands.length > 0 && (
            <div className="filter-group">
              <h4>Brend:</h4>
              <div className="filter-options">
                {brands.map((brand) => (
                  <label key={brand} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand)}
                      onChange={() => handleBrandChange(brand)}
                    />
                    {brand}
                  </label>
                ))}
              </div>
            </div>
          )}

          {Object.entries(attributes).map(([key, values]) => (
            <div className="filter-group" key={key}>
              <h4>{key}:</h4>
              <div className="filter-options">
                {[...values].map((value) => (
                  <label key={`${key}-${value}`} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedAttributes[key]?.includes(value) || false}
                      onChange={() => handleAttributeChange(key, value)}
                    />
                    {value}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <button className="reset-filters" onClick={resetFilters}>
            Resetuj filtere
          </button>
        </div>
      )}

      <div className="products-count">
        Prikazano {currentProducts.length} od {filteredProducts.length} proizvoda
      </div>

        <div className="products-grid">
          {currentProducts.map((product) => (
            <EweProductCard
              key={product.anProductKey}
              product={product}
              cart={cart}
              setCart={setCart}
              isLoggedIn={isLoggedIn}
              refreshCart={refreshCart}
            />
          ))}
        </div>

      {totalPages > 1 && (
        <div className="filter-and-pagination-container" style={{marginTop: '1rem'}}>
          <div className="pagination-container">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Nazad
            </button>
            <span className="pagination-span">Stranica {currentPage} od {totalPages}</span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Napred
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;